/**
 * 공종·공정 탭.
 *
 * 한 화면에 둘을 같이 두는 이유 — 공종을 고칠 때 「이건 언제 하는 일인가」 를
 * 바로 고르게 하려면 공정 목록이 옆에 있어야 한다. 둘은 다른 축이지만 손은 같이 간다.
 *
 * **표준 행도 여기서 고칠 수 있다.** 고치면 원본이 바뀌는 것이 아니라 그 회사
 * 전용 행이 새로 생겨 덮는다 — 다른 회사는 영향을 받지 않고, 안 고친 값
 * (공정·목차 순서)은 표준에서 그대로 물려받는다.
 *
 * 고친 줄만 모아 한 번에 보낸다. 줄마다 요청을 날리면 그중 하나가 실패했을 때
 * 화면과 저장소가 갈린다.
 */

import { useMemo, useState, useTransition } from "react";
import { toast } from "sonner";

import Field from "@/components/field";
import SaveBar, { InstantSaveNote } from "@/components/save-bar";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { Checkbox } from "@buildpy/ui/checkbox";
import { Input } from "@buildpy/ui/input";
import { SimpleSelect } from "@buildpy/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@buildpy/ui/table";
import {
  savePhaseRows,
  saveTradeRows,
} from "@/api/actions/template";
import { usePhone } from "@/modules/quote/ui/editor/mobile/use-phone";
import { TRADE_GROUP_LABEL, TradeGroup } from "@buildpy/core/modules/template/trades";

export interface TradeRowView {
  code: string;
  name: string;
  group: TradeGroup;
  /** 기본 공정의 코드. 없으면 null. */
  phase_code: string | null;
  /** 기본 공정의 이름 — 표시용. */
  phase_name: string | null;
  active: boolean;
  sort_order: number;
  /** true 면 빌드파이 표준 행이다(회사가 아직 안 고쳤다). */
  builtin: boolean;
}

export interface PhaseRowView {
  code: string;
  name: string;
  sort_order: number;
  active: boolean;
  builtin: boolean;
}

// 줄마다 칸의 읽는 이름 앞머리 — 이름이 겹치는 줄만 몇 번째 줄인지 붙여 화면 읽기에서 갈린다.
export const rowLabels = (rows: { code: string; name: string }[]): Map<string, string> => {
  const count = new Map<string, number>();
  for (const row of rows) count.set(row.name, (count.get(row.name) ?? 0) + 1);
  return new Map(
    rows.map((row, index) => [row.code, (count.get(row.name) ?? 0) > 1 ? `${row.name} (${index + 1}번째 줄)` : row.name])
  );
};

interface Props {
  trades: TradeRowView[];
  phases: PhaseRowView[];
  canEdit: boolean;
}

/** 한 줄에서 사람이 고칠 수 있는 것만. 코드는 불변이라 없다. */
interface TradeEdit {
  name: string;
  group: TradeGroup;
  phase_code: string;
  sort_order: number;
  active: boolean;
}

interface PhaseEdit {
  name: string;
  sort_order: number;
  active: boolean;
}

const groupOptions = [
  { value: "BASIC", label: TRADE_GROUP_LABEL.BASIC },
  { value: "STYLE", label: TRADE_GROUP_LABEL.STYLE },
];

const numberClass = "h-8 w-16 text-right tabular-nums";

// 폰 카드 칸 위의 작은 이름 — 칸 자체는 aria-label 로 읽힌다.
const cardLabelClass = "text-xs text-muted-foreground";

const tradeEditOf = (row: TradeRowView): TradeEdit => ({
  name: row.name,
  group: row.group,
  phase_code: row.phase_code ?? "",
  sort_order: row.sort_order,
  active: row.active,
});

const phaseEditOf = (row: PhaseRowView): PhaseEdit => ({
  name: row.name,
  sort_order: row.sort_order,
  active: row.active,
});

const sameTrade = (a: TradeEdit, b: TradeEdit): boolean =>
  a.name === b.name &&
  a.group === b.group &&
  a.phase_code === b.phase_code &&
  a.sort_order === b.sort_order &&
  a.active === b.active;

const samePhase = (a: PhaseEdit, b: PhaseEdit): boolean =>
  a.name === b.name && a.sort_order === b.sort_order && a.active === b.active;

// 새 줄의 코드는 화면에 안 보이므로 여기서 만든다 — 영문·숫자·_ 만(액션의 codeField 규칙).
const newCode = (prefix: "t" | "p"): string =>
  `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const SettingsTrades = ({ trades, phases, canEdit }: Props) => {
  const [pending, startTransition] = useTransition();
  // 서버·첫 그림은 null 이라 표·카드를 다 그리고 CSS 가 가른다 — 그 뒤로는 한 벌만.
  const phone = usePhone();

  /** 고친 줄만. 코드를 키로 둔다 — 코드는 불변이라 행이 바뀌어도 안 흔들린다. */
  const [tradeEdits, setTradeEdits] = useState<Record<string, TradeEdit>>({});
  const [phaseEdits, setPhaseEdits] = useState<Record<string, PhaseEdit>>({});

  const [newTrade, setNewTrade] = useState({
    name: "",
    group: "BASIC" as TradeGroup,
    phase_code: "",
  });
  const [newPhase, setNewPhase] = useState({ name: "" });

  const tradeBase = useMemo(
    () => new Map(trades.map((row) => [row.code, tradeEditOf(row)])),
    [trades]
  );
  const phaseBase = useMemo(
    () => new Map(phases.map((row) => [row.code, phaseEditOf(row)])),
    [phases]
  );
  const tradeLabels = useMemo(() => rowLabels(trades), [trades]);
  const phaseLabels = useMemo(() => rowLabels(phases), [phases]);
  const phaseOptions = useMemo(
    () => [
      { value: "", label: "정하지 않음" },
      ...phases.map((phase) => ({ value: phase.code, label: phase.name })),
    ],
    [phases]
  );

  const tradeOf = (row: TradeRowView): TradeEdit =>
    tradeEdits[row.code] ?? tradeEditOf(row);
  const phaseOf = (row: PhaseRowView): PhaseEdit =>
    phaseEdits[row.code] ?? phaseEditOf(row);

  const patchTrade = (row: TradeRowView, patch: Partial<TradeEdit>) =>
    setTradeEdits((prev) => ({
      ...prev,
      [row.code]: { ...tradeOf(row), ...patch },
    }));

  const patchPhase = (row: PhaseRowView, patch: Partial<PhaseEdit>) =>
    setPhaseEdits((prev) => ({
      ...prev,
      [row.code]: { ...phaseOf(row), ...patch },
    }));

  /** 실제로 값이 달라진 줄만. 눌렀다 되돌린 줄은 빠진다. */
  const changedTrades = Object.entries(tradeEdits).filter(([code, edit]) => {
    const base = tradeBase.get(code);
    return base ? !sameTrade(base, edit) : false;
  });
  const changedPhases = Object.entries(phaseEdits).filter(([code, edit]) => {
    const base = phaseBase.get(code);
    return base ? !samePhase(base, edit) : false;
  });

  const changedCount = changedTrades.length + changedPhases.length;

  // 공종·공정을 한 저장 바로 — 고친 쪽만 차례로 보내고, 앞이 실패하면 뒤는 보내지 않는다.
  const saveAll = () => {
    startTransition(async () => {
      if (changedTrades.length > 0) {
        const result = await saveTradeRows(
          changedTrades.map(([code, edit]) => ({
            code,
            name: edit.name,
            group: edit.group,
            // 빈 값도 보낸다 — 「정하지 않음」 으로 되돌리는 길이다.
            phase_code: edit.phase_code,
            sort_order: edit.sort_order,
            active: edit.active,
          }))
        );
        if (!result.ok) {
          toast.error(result.message);
          return;
        }
        toast.success(result.message);
        setTradeEdits({});
      }
      if (changedPhases.length > 0) {
        const result = await savePhaseRows(
          changedPhases.map(([code, edit]) => ({
            code,
            name: edit.name,
            sort_order: edit.sort_order,
            active: edit.active,
          }))
        );
        if (!result.ok) {
          toast.error(result.message);
          return;
        }
        toast.success(result.message);
        setPhaseEdits({});
      }
    });
  };

  const addTrade = () => {
    startTransition(async () => {
      const result = await saveTradeRows([
        {
          code: newCode("t"),
          name: newTrade.name,
          group: newTrade.group,
          phase_code: newTrade.phase_code || undefined,
        },
      ]);
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      toast.success(result.message);
      setNewTrade({ name: "", group: "BASIC", phase_code: "" });
    });
  };

  const addPhase = () => {
    startTransition(async () => {
      const result = await savePhaseRows([
        { code: newCode("p"), name: newPhase.name },
      ]);
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      toast.success(result.message);
      setNewPhase({ name: "" });
    });
  };

  return (
    <div className="flex flex-col gap-y-8">
      <section className="flex flex-col gap-y-3">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div className="flex flex-col gap-y-1">
            <h2 className="text-base font-semibold">공종 (견적서 목차)</h2>
            <p className="text-sm text-muted-foreground">
              견적서의 목차입니다. 표준 15개는 지울 수 없고,{" "}
              <b>노출을 끄면 새 견적에서만 숨습니다</b> — 이미 쓴 견적의 표기는
              그대로 남습니다.
            </p>
          </div>
        </div>

        {/*
          `min-w` 가 없으면 감싼 칸의 `overflow-x-auto` 가 영영 안 걸린다 — 표가
          `w-full` 이라 좁은 화면에서 가로로 미는 대신 열을 눌러, 「서류대행 / 보양」
          이 「서류대」 로 잘리고 셀렉트는 화살표만 남는다(674px 실측).
        */}
        {phone !== true && (
          <div className="hidden overflow-x-auto rounded-md border border-border md:block">
            <Table className="min-w-[46rem]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16 px-3 py-2">순서</TableHead>
                  <TableHead className="px-3 py-2">공종</TableHead>
                  <TableHead className="px-3 py-2">비용 분류</TableHead>
                  <TableHead className="px-3 py-2">기본 공정</TableHead>
                  <TableHead className="w-20 px-3 py-2">노출</TableHead>
                  <TableHead className="px-3 py-2">출처</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trades.map((row) => {
                  const edit = tradeOf(row);
                  const name = tradeLabels.get(row.code) ?? row.name;
                  return (
                    <TableRow key={row.code}>
                      <TableCell className="px-3 py-1.5">
                        <Input
                          aria-label={`${name} 순서`}
                          className={numberClass}
                          disabled={!canEdit}
                          name={`trade_order_${row.code}`}
                          type="number"
                          value={String(edit.sort_order)}
                          onChange={(e) =>
                            patchTrade(row, {
                              sort_order: Number(e.target.value) || 0,
                            })
                          }
                        />
                      </TableCell>
                      <TableCell className="px-3 py-1.5">
                        <Input
                          aria-label={`${name} 이름`}
                          className="h-8"
                          disabled={!canEdit}
                          name={`trade_name_${row.code}`}
                          value={edit.name}
                          onChange={(e) =>
                            patchTrade(row, { name: e.target.value })
                          }
                        />
                      </TableCell>
                      <TableCell className="px-3 py-1.5">
                        <SimpleSelect
                          aria-label={`${name} 비용 분류`}
                          arrowKeysChangeValue
                          disabled={!canEdit}
                          options={groupOptions}
                          size="xs"
                          value={edit.group}
                          onValueChange={(next) =>
                            patchTrade(row, { group: next as TradeGroup })
                          }
                        />
                      </TableCell>
                      <TableCell className="px-3 py-1.5">
                        <SimpleSelect
                          aria-label={`${name} 기본 공정`}
                          arrowKeysChangeValue
                          disabled={!canEdit}
                          options={phaseOptions}
                          size="xs"
                          value={edit.phase_code}
                          onValueChange={(next) =>
                            patchTrade(row, { phase_code: next })
                          }
                        />
                      </TableCell>
                      <TableCell className="px-3 py-1.5">
                        <Checkbox
                          aria-label={`${name} 노출`}
                          checked={edit.active}
                          disabled={!canEdit}
                          onCheckedChange={(next) =>
                            patchTrade(row, { active: next === true })
                          }
                        />
                      </TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-1.5 text-xs text-muted-foreground">
                        {row.builtin ? "buildpy 표준" : "우리 회사"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {/* 폰(md 미만)은 줄마다 카드 — 표는 가로로 밀어야 셀렉트·노출이 보였다. */}
        {phone !== false && (
          <ul aria-label="공종 목록" className="flex flex-col gap-y-2 md:hidden" data-testid="trade-cards">
            {trades.map((row) => {
              const edit = tradeOf(row);
              const name = tradeLabels.get(row.code) ?? row.name;
              return (
                <SectionCard key={row.code} as="li" className="flex flex-col gap-y-2" padding="sm">
                  <div className="flex items-center justify-between gap-x-3 text-xs text-muted-foreground">
                    <span>{row.builtin ? "buildpy 표준" : "우리 회사"}</span>
                    <span className="flex items-center gap-x-2">
                      <span aria-hidden>노출</span>
                      <Checkbox
                        aria-label={`${name} 노출`}
                        checked={edit.active}
                        disabled={!canEdit}
                        onCheckedChange={(next) => patchTrade(row, { active: next === true })}
                      />
                    </span>
                  </div>
                  <div className="grid grid-cols-[minmax(0,1fr)_4rem] gap-x-2 gap-y-1">
                    <span aria-hidden className={cardLabelClass}>이름</span>
                    <span aria-hidden className={cardLabelClass}>순서</span>
                    <Input
                      aria-label={`${name} 이름`}
                      disabled={!canEdit}
                      name={`trade_name_${row.code}`}
                      value={edit.name}
                      onChange={(e) => patchTrade(row, { name: e.target.value })}
                    />
                    <Input
                      aria-label={`${name} 순서`}
                      className="text-right tabular-nums"
                      disabled={!canEdit}
                      name={`trade_order_${row.code}`}
                      type="number"
                      value={String(edit.sort_order)}
                      onChange={(e) => patchTrade(row, { sort_order: Number(e.target.value) || 0 })}
                    />
                  </div>
                  {/* 셋을 한 줄에 두면 폰 16px 글자에서 「스타일 공사」 가 잘린다 — 두 칸씩 나눈다 */}
                  <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                    <span aria-hidden className={cardLabelClass}>비용 분류</span>
                    <span aria-hidden className={cardLabelClass}>기본 공정</span>
                    <SimpleSelect
                      aria-label={`${name} 비용 분류`}
                      disabled={!canEdit}
                      options={groupOptions}
                      value={edit.group}
                      onValueChange={(next) => patchTrade(row, { group: next as TradeGroup })}
                    />
                    <SimpleSelect
                      aria-label={`${name} 기본 공정`}
                      disabled={!canEdit}
                      options={phaseOptions}
                      value={edit.phase_code}
                      onValueChange={(next) => patchTrade(row, { phase_code: next })}
                    />
                  </div>
                </SectionCard>
              );
            })}
          </ul>
        )}

        {canEdit && (
          <div className="flex flex-wrap items-end gap-2 rounded-md border border-border bg-muted/30 p-3">
            <Field label="이름">
              <Input
                className="w-48"
                name="new_trade_name"
                placeholder="예: 중문 공사"
                value={newTrade.name}
                onChange={(e) =>
                  setNewTrade((p) => ({ ...p, name: e.target.value }))
                }
              />
            </Field>
            <Field label="비용 분류">
              <SimpleSelect
                aria-label="비용 분류"
                className="w-32"
                options={groupOptions}
                value={newTrade.group}
                onValueChange={(next) =>
                  setNewTrade((p) => ({ ...p, group: next as TradeGroup }))
                }
              />
            </Field>
            <Field label="기본 공정">
              <SimpleSelect
                aria-label="기본 공정"
                className="w-32"
                options={phaseOptions}
                value={newTrade.phase_code}
                onValueChange={(next) =>
                  setNewTrade((p) => ({ ...p, phase_code: next }))
                }
              />
            </Field>
            <Button
              disabled={
                pending || newTrade.name.trim() === ""
              }
              size="sm"
              variant="outline"
              onClick={addTrade}>
              공종 추가
            </Button>
            <InstantSaveNote>추가하면 바로 저장됩니다</InstantSaveNote>
          </div>
        )}
      </section>

      <section className="flex flex-col gap-y-3">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div className="flex flex-col gap-y-1">
            <h2 className="text-base font-semibold">공정 (시공 순서)</h2>
            <p className="text-sm text-muted-foreground">
              현장의 시공 순서입니다. 견적서 목차와는 다른 축이라 따로 둡니다 —
              도배는 목차에서 9번이지만 시공은 거의 끝입니다.
            </p>
          </div>
        </div>

        {phone !== true && (
          <div className="hidden overflow-x-auto rounded-md border border-border md:block">
            <Table className="min-w-[26rem]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16 px-3 py-2">순서</TableHead>
                  <TableHead className="px-3 py-2">공정</TableHead>
                  <TableHead className="w-20 px-3 py-2">노출</TableHead>
                  <TableHead className="px-3 py-2">출처</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {phases.map((row) => {
                  const edit = phaseOf(row);
                  const name = phaseLabels.get(row.code) ?? row.name;
                  return (
                    <TableRow key={row.code}>
                      <TableCell className="px-3 py-1.5">
                        <Input
                          aria-label={`${name} 순서`}
                          className={numberClass}
                          disabled={!canEdit}
                          name={`phase_order_${row.code}`}
                          type="number"
                          value={String(edit.sort_order)}
                          onChange={(e) =>
                            patchPhase(row, {
                              sort_order: Number(e.target.value) || 0,
                            })
                          }
                        />
                      </TableCell>
                      <TableCell className="px-3 py-1.5">
                        <Input
                          aria-label={`${name} 이름`}
                          className="h-8"
                          disabled={!canEdit}
                          name={`phase_name_${row.code}`}
                          value={edit.name}
                          onChange={(e) =>
                            patchPhase(row, { name: e.target.value })
                          }
                        />
                      </TableCell>
                      <TableCell className="px-3 py-1.5">
                        <Checkbox
                          aria-label={`${name} 노출`}
                          checked={edit.active}
                          disabled={!canEdit}
                          onCheckedChange={(next) =>
                            patchPhase(row, { active: next === true })
                          }
                        />
                      </TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-1.5 text-xs text-muted-foreground">
                        {row.builtin ? "buildpy 표준" : "우리 회사"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {phone !== false && (
          <ul aria-label="공정 목록" className="flex flex-col gap-y-2 md:hidden" data-testid="phase-cards">
            {phases.map((row) => {
              const edit = phaseOf(row);
              const name = phaseLabels.get(row.code) ?? row.name;
              return (
                <SectionCard key={row.code} as="li" className="flex flex-col gap-y-2" padding="sm">
                  <div className="flex items-center justify-between gap-x-3 text-xs text-muted-foreground">
                    <span>{row.builtin ? "buildpy 표준" : "우리 회사"}</span>
                    <span className="flex items-center gap-x-2">
                      <span aria-hidden>노출</span>
                      <Checkbox
                        aria-label={`${name} 노출`}
                        checked={edit.active}
                        disabled={!canEdit}
                        onCheckedChange={(next) => patchPhase(row, { active: next === true })}
                      />
                    </span>
                  </div>
                  <div className="grid grid-cols-[4rem_minmax(0,1fr)] gap-x-2 gap-y-1">
                    <span aria-hidden className={cardLabelClass}>순서</span>
                    <span aria-hidden className={cardLabelClass}>공정</span>
                    <Input
                      aria-label={`${name} 순서`}
                      className="text-right tabular-nums"
                      disabled={!canEdit}
                      name={`phase_order_${row.code}`}
                      type="number"
                      value={String(edit.sort_order)}
                      onChange={(e) => patchPhase(row, { sort_order: Number(e.target.value) || 0 })}
                    />
                    <Input
                      aria-label={`${name} 이름`}
                      disabled={!canEdit}
                      name={`phase_name_${row.code}`}
                      value={edit.name}
                      onChange={(e) => patchPhase(row, { name: e.target.value })}
                    />
                  </div>
                </SectionCard>
              );
            })}
          </ul>
        )}

        {canEdit && (
          <div className="flex flex-wrap items-end gap-2 rounded-md border border-border bg-muted/30 p-3">
            <Field label="이름">
              <Input
                className="w-48"
                name="new_phase_name"
                placeholder="예: 입주청소"
                value={newPhase.name}
                onChange={(e) =>
                  setNewPhase((p) => ({ ...p, name: e.target.value }))
                }
              />
            </Field>
            <Button
              disabled={
                pending || newPhase.name.trim() === ""
              }
              size="sm"
              variant="outline"
              onClick={addPhase}>
              공정 추가
            </Button>
            <InstantSaveNote>추가하면 바로 저장됩니다</InstantSaveNote>
          </div>
        )}
      </section>

      {canEdit && (
        <SaveBar
          dirty={changedCount > 0}
          label={changedCount > 0 ? `${changedCount}줄 저장` : "저장"}
          onSave={saveAll}
          pending={pending}
        />
      )}
    </div>
  );
};

export default SettingsTrades;
